import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ApiException,
    ScenarioRunClient,
    ScenarioRunConfigurationRestore,
    ScenarioRunProfile,
    ScenarioRunScenario,
    ScenarioRunState,
} from '../api/api';
import { apiBaseUrl } from '../api/baseUrl';
import {
    Breadcrumbs,
    environmentCrumbs,
} from '../components/Breadcrumbs/Breadcrumbs';
import { ScenarioCleanup } from '../components/ScenarioRun/ScenarioCleanup';
import { ScenarioPreflight } from '../components/ScenarioRun/ScenarioPreflight';
import { ScenarioRunDiagnostics } from '../components/ScenarioRun/ScenarioRunDiagnostics';
import { ScenarioRunStatus } from '../components/ScenarioRun/ScenarioRunStatus';
import { StopRunButton } from '../components/ScenarioRun/StopRunButton';
import {
    Outcome,
    outcomeOfScenario,
} from '../components/ScenarioRun/ScenarioRunTable';
import { RunSummary } from '../components/ScenarioRun/RunDetail/RunSummary';
import {
    LogLines,
    ScenarioCard,
} from '../components/ScenarioRun/RunDetail/ScenarioCard';
import { isInRun } from '../components/ScenarioRun/RunDetail/runDetailModel';
import { ExactLimits, useExactLimitsBoard } from '../components/ScenarioRun/RunDetail/ExactLimits';
import { mergeLimits } from '../components/ScenarioRun/RunDetail/exactLimitsModel';
import { followTarget } from '../components/ScenarioRun/RunDetail/followModel';
import { EnvironmentPill, RunStickyBar } from '../components/ScenarioRun/RunDetail/RunStickyBar';
import { FOLLOW_ATTRIBUTE, useFollowRun } from '../components/ScenarioRun/RunDetail/useFollowRun';
import '../components/ScenarioRun/RunDetail/RunDetail.scss';
import '../components/ScenarioRun/RunDetail/RunFollow.scss';
import { useAxiosSwagger } from '../hooks/useAxiosSwagger';
import { runBuildLabel, runIsFinished, runMoment } from '../utils/scenarioRunFormat';
import { scenarioTestsPath } from '../utils/paths';
import { streamScenarioRun } from '../utils/scenarioRunStream';
import { loadingStateType } from './dashboard';
import { useBusy } from '../hooks/useBusy';

// A run the dashboard started is followed live, one state per change. This interval is only for a run it cannot
// stream - one started from the command line, or carried by another instance - which is read from its state
// document instead. Polling stops as soon as the run has a finish time.
const POLL_WHILE_RUNNING_MS = 2000;

// A run that was just started has no state document yet: the page keeps asking for it this long before it gives up.
const WAIT_FOR_STARTED_RUN_MS = 180000;

// The run log can run to hundreds of lines on a long run; the newest are the ones anybody reads.
const RUN_LOG_LINES = 200;

/** What the scenarios page hands over when it opens a run it has just started. */
export interface StartedRunNavigation {
    startedAt: number;
}

type Filter = 'run' | 'attention' | 'passed' | 'notInRun';

const FILTERS: {
    id: Filter;
    label: string;
    matches: (scenario: ScenarioRunScenario, outcome: Outcome) => boolean;
}[] = [
    { id: 'run', label: 'In this run', matches: (scenario) => isInRun(scenario) },
    {
        id: 'attention',
        label: 'Needs a look',
        matches: (scenario, outcome) => isInRun(scenario) && (outcome === 'failed' || outcome === 'warnings'),
    },
    {
        id: 'passed',
        label: 'Passed',
        matches: (scenario, outcome) => isInRun(scenario) && outcome === 'passed',
    },
    { id: 'notInRun', label: 'Not in this run', matches: (scenario) => !isInRun(scenario) },
];

/**
 * What became of the configuration the environment held before the run: every profile is written on top of it,
 * and it is put back when the run ends, also when the run is stopped.
 */
const ConfigurationRestoreNote = ({
    restore,
}: {
    restore?: ScenarioRunConfigurationRestore;
}) => {
    if (!restore) {
        return null;
    }

    const changes = restore.changes ?? [];
    const failed = restore.status === 'failed';
    const text =
        restore.status === 'restored'
            ? changes.length === 0
                ? 'The configuration from before the run was already in place when the run ended.'
                : `The configuration from before the run was restored: ${changes.length} row(s) set back.`
            : failed
            ? `The configuration from before the run could not be restored (${restore.detail}). The next run, or scenario-runner restore-configuration, restores it.`
            : 'The configuration from before the run is restored when the run ends.';

    return (
        <details className={failed ? 'rd-restore rd-restore--failed' : 'rd-restore'} open={failed}>
            <summary>
                <FontAwesomeIcon
                    icon={failed ? 'triangle-exclamation' : restore.status === 'restored' ? 'rotate-left' : 'hourglass-half'}
                />{' '}
                {text}
                {restore.takenOverFromRunId &&
                    ` It is the configuration from before run ${restore.takenOverFromRunId}, which never got to restore it.`}
            </summary>
            {changes.length > 0 && (
                <div className="rd-group__config">
                    <div>
                        {changes.map((change) => (
                            <code key={change}>{change}</code>
                        ))}
                    </div>
                </div>
            )}
        </details>
    );
};

/**
 * The scenarios that ran under one profile, under a line saying which configuration that was.
 *
 * The profile is what a scenario's outcome depends on besides its own steps, so it heads the group rather than
 * repeating on every card.
 */
const ProfileGroup = ({
    profileId,
    profile,
    scenarios,
    events,
    folded,
}: {
    profileId: string;
    profile?: ScenarioRunProfile;
    scenarios: ScenarioRunScenario[];
    events: ScenarioRunState['events'];
    folded: Set<string>;
}) => {
    const configuration = profile?.appliedConfiguration ?? [];

    return (
        <section className="rd-group">
            <header className="rd-group__head">
                <span>Profile</span>
                <code className="rd-group__id">{profileId}</code>
                {profile?.description && <span>{profile.description}</span>}
                {profile?.featuresFrom && (
                    <span title="The feature flags this profile ran with were read from this environment when the run started">
                        · features from <code>{profile.featuresFrom}</code>
                        {profile.featuresReadAtUtc && `, read ${new Date(profile.featuresReadAtUtc).toLocaleString()}`}
                    </span>
                )}
                {profile && !profile.cacheCleared && (
                    <span className="rd-group__warn" title="The function apps may still have run with the previous configuration.">
                        <FontAwesomeIcon icon="triangle-exclamation" /> not picked up
                    </span>
                )}
                {configuration.length > 0 && (
                    <details className="rd-group__config">
                        <summary>configuration ({configuration.length})</summary>
                        <div>
                            {configuration.map((row) => (
                                <code key={row}>{row}</code>
                            ))}
                        </div>
                    </details>
                )}
            </header>
            <ul className="rd-group__list">
                {scenarios.map((scenario) => (
                    <ScenarioCard
                        key={scenario.id}
                        scenario={scenario}
                        events={(events ?? []).filter((event) => event.scenarioId === scenario.id)}
                        collapsed={folded.has(scenario.id as string)}
                    />
                ))}
            </ul>
        </section>
    );
};

/** The scenarios the run did not execute, one line each: they are listed so the suite reads complete. */
const NotInRun = ({ scenarios }: { scenarios: ScenarioRunScenario[] }) => (
    <ul className="rd-skipped">
        {scenarios.map((scenario) => (
            <li className="rd-skipped__item" key={scenario.id}>
                <code className="rd-skipped__id">{scenario.id}</code>
                <span className="rd-skipped__description">{scenario.description}</span>
                <span className="rd-skipped__reason">{scenario.skipReason}</span>
            </li>
        ))}
    </ul>
);

export const ScenarioRunDetail = () => {
    const { customerID, environment, runId } = useParams();
    const location = useLocation();
    const startedAt = (location.state as StartedRunNavigation | null)?.startedAt;

    const [loadingState, setLoadingState] = useState<loadingStateType>('init');
    // True while the run this page was opened for has been started but has not written its state yet.
    const [starting, setStarting] = useState(false);

    useBusy(loadingState === 'loading', 'Loading the run');
    const [state, setState] = useState<ScenarioRunState | undefined>(undefined);
    const [filter, setFilter] = useState<Filter>('run');

    // True while the page follows the run live; false when it has to read the state document instead.
    const [live, setLive] = useState<boolean>(true);
    const [canStop, setCanStop] = useState<boolean>(false);

    const axios = useAxiosSwagger();

    const load = useCallback(async () => {
        if (!axios) {
            return;
        }

        try {
            const client = new ScenarioRunClient(undefined, axios);
            const response = await client.scenarioRun(
                customerID as string,
                environment as string,
                runId as string
            );

            setState(response);
            // Kept apart from the state: the live feed sends the runner's own document, which knows nothing of
            // which dashboard carries the run, and would otherwise take the button away with its first update.
            setCanStop(!!response.canStop);
            setStarting(false);
            setLoadingState('loaded');
        } catch (error) {
            // A run that was just started writes its first state a moment later; until then the page waits for it.
            const notYet =
                ApiException.isApiException(error) &&
                error.status === 404 &&
                !!startedAt &&
                Date.now() - startedAt < WAIT_FOR_STARTED_RUN_MS;

            if (notYet) {
                setStarting(true);

                return;
            }

            console.log(error);
            setStarting(false);
            // A blip while polling must not throw away the run that is already on screen.
            setLoadingState((current) =>
                current === 'loaded' ? current : 'error'
            );
        }
    }, [axios, customerID, environment, runId, startedAt]);

    useEffect(() => {
        setLoadingState('loading');
        load();
    }, [load]);

    useEffect(() => {
        if (!starting) {
            return;
        }

        const timer = setInterval(load, POLL_WHILE_RUNNING_MS);

        return () => clearInterval(timer);
    }, [starting, load]);

    const finished = runIsFinished(state?.finishedAtUtc);

    // Follow the run live while it goes. When the API does not carry it, or the connection drops, the page reads
    // the state document once more and carries on by polling it.
    useEffect(() => {
        if (finished || !live || !axios || loadingState !== 'loaded') {
            return;
        }

        const abort = new AbortController();
        const authorization = axios.defaults.headers.common['Authorization'] as
            | string
            | undefined;
        const url = `${apiBaseUrl}/ScenarioRun/${encodeURIComponent(
            customerID as string
        )}/${encodeURIComponent(environment as string)}/${encodeURIComponent(
            runId as string
        )}/live`;

        streamScenarioRun(url, authorization, setState, abort.signal).then(
            (outcome) => {
                if (abort.signal.aborted) {
                    return;
                }

                if (outcome !== 'ended') {
                    setLive(false);
                }

                load();
            }
        );

        return () => abort.abort();
    }, [finished, live, axios, loadingState, customerID, environment, runId, load]);

    // A run that has finished never changes again, so the poll is torn down rather than left ticking.
    useEffect(() => {
        if (finished || live) {
            return;
        }

        const timer = setInterval(load, POLL_WHILE_RUNNING_MS);

        return () => clearInterval(timer);
    }, [finished, live, load]);

    const events = useMemo(() => state?.events ?? [], [state]);
    const runEvents = useMemo(
        () => events.filter((event) => !event.scenarioId),
        [events]
    );
    const scenarios = useMemo(() => state?.scenarios ?? [], [state]);
    const outcomes = useMemo(
        () => scenarios.map((scenario) => outcomeOfScenario(scenario).outcome as Outcome),
        [scenarios]
    );

    // The scenarios as the page shows them: the filter's, grouped by profile in the order the run met them.
    const groups = useMemo(() => {
        const matches = FILTERS.find((candidate) => candidate.id === filter)?.matches;

        return scenarios
            .filter((scenario, index) => matches?.(scenario, outcomes[index]))
            .reduce<{ profileId: string; scenarios: ScenarioRunScenario[] }[]>((all, scenario) => {
                const profileId = scenario.profile ?? '';
                const group =
                    all.find((candidate) => candidate.profileId === profileId) ??
                    all[all.push({ profileId, scenarios: [] }) - 1];

                group.scenarios.push(scenario);

                return all;
            }, []);
    }, [scenarios, outcomes, filter]);

    const target =
        filter === 'notInRun' ? undefined : followTarget(groups.flatMap((group) => group.scenarios));

    // The head of the run stays at the top of the screen once the page has scrolled past the real one.
    const [header, setHeader] = useState<HTMLElement | null>(null);
    const [headerGone, setHeaderGone] = useState(false);
    const sticky = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!header) return;

        // On scroll rather than with an IntersectionObserver: that one skipped changes while the page glided along.
        const check = () => setHeaderGone(header.getBoundingClientRect().bottom < 0);

        check();
        window.addEventListener('scroll', check, { passive: true });

        return () => window.removeEventListener('scroll', check);
    }, [header]);

    // Exact's limits as everyone using the environment's token was last told, while the run goes; afterwards what
    // the run itself last read.
    const board = useExactLimitsBoard(
        customerID as string,
        environment as string,
        loadingState === 'loaded' && !finished
    );
    const limits = useMemo(
        () => mergeLimits(state?.exactLimits, finished ? undefined : board),
        [state?.exactLimits, board, finished]
    );

    const { following, follow } = useFollowRun({
        targetId: target,
        active: loadingState === 'loaded' && !finished,
        topOffset: () => (sticky.current?.offsetHeight ?? 0) + 12,
    });

    // Following the run, the page folds a scenario shut once it is done and the run has moved on to the next. Not
    // straight away: only once the page has glided past it, so it never folds where the person is looking.
    const [folded, setFolded] = useState<Set<string>>(() => new Set());
    const [toFold, setToFold] = useState<string[]>([]);
    const lastFollowed = useRef<string>();

    useEffect(() => {
        if (!target) return;

        const previous = lastFollowed.current;

        lastFollowed.current = target;

        if (!following || !previous || previous === target) return;

        setToFold((current) => (current.includes(previous) ? current : [...current, previous]));
    }, [target, following]);

    useEffect(() => {
        if (!following || toFold.length === 0) return;

        const check = () => {
            const above = toFold.filter((id) => {
                const card = document.querySelector(`[${FOLLOW_ATTRIBUTE}="${CSS.escape(id)}"]`);

                return !card || card.getBoundingClientRect().bottom <= (sticky.current?.offsetHeight ?? 0);
            });

            if (above.length === 0) return;

            setToFold((current) => current.filter((id) => !above.includes(id)));
            setFolded((current) => new Set([...Array.from(current), ...above]));
        };

        check();
        window.addEventListener('scroll', check, { passive: true });

        return () => window.removeEventListener('scroll', check);
    }, [toFold, following]);

    const crumbs = [
        ...environmentCrumbs(customerID, environment),
        {
            label: 'Scenario tests',
            to: scenarioTestsPath(customerID, environment),
        },
        { label: runId ?? '' },
    ];

    if (starting && !state) {
        return (
            <main className="dashboard-detail scenario-tests rd-page">
                <Breadcrumbs items={crumbs} />
                <div className="rd-waiting" role="status">
                    <FontAwesomeIcon icon="spinner" spin />
                    Run {runId} is starting. This page follows it as soon as it writes its first state.
                </div>
            </main>
        );
    }

    if (loadingState === 'loading' || loadingState === 'init') {
        return (
            <main className="dashboard-detail scenario-tests rd-page">
                <Breadcrumbs items={crumbs} />
                <p className="scenario-tests__muted">
                    <FontAwesomeIcon icon="spinner" spin /> Loading run {runId}
                    ...
                </p>
            </main>
        );
    }

    if (loadingState === 'error' || !state) {
        return (
            <main className="dashboard-detail scenario-tests rd-page">
                <Breadcrumbs items={crumbs} />
                <div className="alert alert-warning" role="status">
                    {startedAt
                        ? `Run ${runId} was started but has not written any state in three minutes. The runner probably failed before it got going; the reason is in the log of the API that started it.`
                        : `Run ${runId} could not be read for ${customerID} ${environment}.`}
                </div>
                <Link
                    to={scenarioTestsPath(customerID, environment)}
                    className="btn btn-outline-secondary"
                >
                    Back to the scenario tests
                </Link>
            </main>
        );
    }

    const matching = (id: Filter) =>
        scenarios.filter((scenario, index) =>
            FILTERS.find((candidate) => candidate.id === id)?.matches(scenario, outcomes[index])
        );
    const shown = matching(filter);
    const profiles = state.profiles ?? [];
    const preflight = state.preflight ?? [];

    const runErrors = runEvents.filter((event) => event.level === 'error').length;
    const runWarnings = runEvents.filter((event) => event.level === 'warning').length;

    return (
        <main className="dashboard-detail scenario-tests rd-page">
            <RunStickyBar
                ref={sticky}
                state={state}
                visible={headerGone}
                finished={finished}
                canStop={canStop}
                customer={customerID as string}
                environment={environment as string}
                runId={runId as string}
                limits={limits}
            />
            <header className="dashboard-detail__header" ref={setHeader}>
                <Breadcrumbs items={crumbs} />
                <h2 className="dashboard-detail__title">
                    {state.suiteName}
                    <ScenarioRunStatus status={state.status} />
                    {state.dryRun && (
                        <span className="arl-rundetail__chip">dry run</span>
                    )}
                </h2>
                <p className="rd-head__meta">
                    <EnvironmentPill target={state.target} />
                    <span>started {runMoment(state.startedAtUtc)}</span>
                    {state.buildVersion && (
                        <span>
                            build <code title={state.buildVersion}>{runBuildLabel(state.buildVersion)}</code>
                        </span>
                    )}
                    <span>
                        run <code>{state.runId}</code>
                    </span>
                </p>
                <ExactLimits limits={limits} running={!finished} />
                <div className="arl-rundetail__tools">
                    {canStop && !finished && (
                        <StopRunButton
                            customer={customerID as string}
                            environment={environment as string}
                            runId={runId as string}
                        />
                    )}
                    <ScenarioRunDiagnostics
                        customer={customerID as string}
                        environment={environment as string}
                        runId={runId as string}
                    />
                    {/* A dry run creates nothing, and a run that is going is still using what it created. */}
                    {finished && !state.dryRun && (
                        <ScenarioCleanup
                            customer={customerID as string}
                            environment={environment as string}
                            runId={runId as string}
                        />
                    )}
                </div>
                {state.dryRun && (
                    <p className="rd-head__dry">
                        Dry run: Chargebee and Exact were simulated, so this
                        says nothing about the connector.
                    </p>
                )}
            </header>

            <RunSummary state={state} finished={finished} live={live} />

            {preflight.length > 0 && (
                <div className="arl-rundetail__block">
                    <ScenarioPreflight checks={preflight} />
                </div>
            )}

            <section className="rd-scenarios">
                <div className="rd-scenarios__bar">
                    <h3 className="rd-scenarios__title">Scenarios</h3>
                    <div className="rd-filter" role="group" aria-label="Show scenarios">
                        {FILTERS.map((candidate) => {
                            const count = matching(candidate.id).length;

                            return (
                                <button
                                    key={candidate.id}
                                    type="button"
                                    className={`rd-filter__btn${filter === candidate.id ? ' rd-filter__btn--active' : ''}`}
                                    aria-pressed={filter === candidate.id}
                                    disabled={count === 0 && filter !== candidate.id}
                                    onClick={() => setFilter(candidate.id)}
                                >
                                    {candidate.label}
                                    <span className="rd-filter__count">{count}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {shown.length === 0 ? (
                    <p className="rd-scenarios__empty">No scenario in this run matches.</p>
                ) : filter === 'notInRun' ? (
                    <NotInRun scenarios={shown} />
                ) : (
                    groups.map((group) => (
                        <ProfileGroup
                            key={group.profileId}
                            profileId={group.profileId}
                            profile={profiles.find((profile) => profile.id === group.profileId)}
                            scenarios={group.scenarios}
                            events={events}
                            folded={folded}
                        />
                    ))
                )}
            </section>

            {!following && !finished && target && (
                <button type="button" className="rd-follow" onClick={follow}>
                    <FontAwesomeIcon icon="arrow-down" /> Follow the run
                </button>
            )}

            <div className="rd-foot">
                <ConfigurationRestoreNote restore={state.configurationRestore} />
                <details>
                    <summary>
                        Run log: {runEvents.length} line(s)
                        {runErrors > 0 && ` · ${runErrors} error(s)`}
                        {runWarnings > 0 && ` · ${runWarnings} warning(s)`}
                    </summary>
                    {runEvents.length === 0 ? (
                        <p className="scenario-tests__muted mb-0">Nothing reported yet.</p>
                    ) : (
                        <LogLines events={runEvents.slice(-RUN_LOG_LINES)} />
                    )}
                </details>
            </div>
        </main>
    );
};
